import { useState, useEffect, useRef, useCallback } from "react";
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from '../../context/ThemeContext';

// Sorting algorithm implementations (same as before)
const sortingAlgorithms = {

  insertion: (arr, callback) => {
    const steps = [];
    const workingArr = [...arr];

    for (let i = 1; i < workingArr.length; i++) {
      const key = workingArr[i];
      let j = i - 1;

      steps.push({ arr: [...workingArr], comparing: [i] });

      while (j >= 0 && workingArr[j] > key) {
        steps.push({ arr: [...workingArr], comparing: [j, j + 1] });
        workingArr[j + 1] = workingArr[j];
        j = j - 1;
      }
      workingArr[j + 1] = key;
      steps.push({ arr: [...workingArr], sorted: Array.from({ length: i + 1 }, (_, k) => k) });
    }

    return steps;
  },

  selection: (arr, callback) => {
    const steps = [];
    const workingArr = [...arr];

    for (let i = 0; i < workingArr.length - 1; i++) {
      let minIdx = i;
      steps.push({ arr: [...workingArr], comparing: [i] });

      for (let j = i + 1; j < workingArr.length; j++) {
        steps.push({ arr: [...workingArr], comparing: [minIdx, j] });
        if (workingArr[j] < workingArr[minIdx]) {
          minIdx = j;
        }
      }

      if (minIdx !== i) {
        [workingArr[i], workingArr[minIdx]] = [workingArr[minIdx], workingArr[i]];
      }
      steps.push({ arr: [...workingArr], sorted: Array.from({ length: i + 1 }, (_, k) => k) });
    }

    return steps;
  },

  bubble: (arr, callback) => {
    const steps = [];
    const workingArr = [...arr];

    for (let i = 0; i < workingArr.length - 1; i++) {
      for (let j = 0; j < workingArr.length - i - 1; j++) {
        steps.push({ arr: [...workingArr], comparing: [j, j + 1] });

        if (workingArr[j] > workingArr[j + 1]) {
          [workingArr[j], workingArr[j + 1]] = [workingArr[j + 1], workingArr[j]];
          steps.push({ arr: [...workingArr], comparing: [j, j + 1] });
        }
      }
      steps.push({ arr: [...workingArr], sorted: Array.from({ length: i + 1 }, (_, k) => workingArr.length - 1 - k) });
    }

    return steps;
  },

  shell: (arr, callback) => {
    const steps = [];
    const workingArr = [...arr];

    for (let gap = Math.floor(workingArr.length / 2); gap > 0; gap = Math.floor(gap / 2)) {
      for (let i = gap; i < workingArr.length; i++) {
        const temp = workingArr[i];
        let j = i;

        steps.push({ arr: [...workingArr], comparing: [i] });

        while (j >= gap && workingArr[j - gap] > temp) {
          steps.push({ arr: [...workingArr], comparing: [j, j - gap] });
          workingArr[j] = workingArr[j - gap];
          j -= gap;
        }
        workingArr[j] = temp;
      }
    }

    return steps;
  },

  merge: (arr, callback) => {
    const steps = [];
    const workingArr = [...arr];

    const merge = (left, mid, right) => {
      const leftArr = workingArr.slice(left, mid + 1);
      const rightArr = workingArr.slice(mid + 1, right + 1);

      let i = 0,
        j = 0,
        k = left;

      while (i < leftArr.length && j < rightArr.length) {
        steps.push({ arr: [...workingArr], comparing: [left + i, mid + 1 + j] });

        if (leftArr[i] <= rightArr[j]) {
          workingArr[k] = leftArr[i];
          i++;
        } else {
          workingArr[k] = rightArr[j];
          j++;
        }
        k++;
      }

      while (i < leftArr.length) {
        workingArr[k] = leftArr[i];
        i++;
        k++;
      }

      while (j < rightArr.length) {
        workingArr[k] = rightArr[j];
        j++;
        k++;
      }
    };

    const mergeSort = (left, right) => {
      if (left < right) {
        const mid = Math.floor((left + right) / 2);
        mergeSort(left, mid);
        mergeSort(mid + 1, right);
        merge(left, mid, right);
      }
    };

    mergeSort(0, workingArr.length - 1);
    return steps;
  },

  heap: (arr, callback) => {
    const steps = [];
    const workingArr = [...arr];

    const heapify = (n, i) => {
      let largest = i;
      const left = 2 * i + 1;
      const right = 2 * i + 2;

      if (left < n && workingArr[left] > workingArr[largest]) {
        largest = left;
      }

      if (right < n && workingArr[right] > workingArr[largest]) {
        largest = right;
      }

      if (largest !== i) {
        steps.push({ arr: [...workingArr], comparing: [i, largest] });
        [workingArr[i], workingArr[largest]] = [workingArr[largest], workingArr[i]];
        heapify(n, largest);
      }
    };

    for (let i = Math.floor(workingArr.length / 2) - 1; i >= 0; i--) {
      heapify(workingArr.length, i);
    }

    for (let i = workingArr.length - 1; i > 0; i--) {
      steps.push({ arr: [...workingArr], comparing: [0, i] });
      [workingArr[0], workingArr[i]] = [workingArr[i], workingArr[0]];
      heapify(i, 0);
    }

    return steps;
  },

  quick: (arr, callback) => {
    const steps = [];
    const workingArr = [...arr];

    const partition = (low, high) => {
      const pivot = workingArr[high];
      let i = low - 1;

      for (let j = low; j < high; j++) {
        steps.push({ arr: [...workingArr], comparing: [j, high] });

        if (workingArr[j] < pivot) {
          i++;
          [workingArr[i], workingArr[j]] = [workingArr[j], workingArr[i]];
        }
      }
      [workingArr[i + 1], workingArr[high]] = [workingArr[high], workingArr[i + 1]];
      return i + 1;
    };

    const quickSort = (low, high) => {
      if (low < high) {
        const pi = partition(low, high);
        quickSort(low, pi - 1);
        quickSort(pi + 1, high);
      }
    };

    quickSort(0, workingArr.length - 1);
    return steps;
  },

  quick3: (arr, callback) => {
    const steps = [];
    const workingArr = [...arr];

    const partition3 = (low, high) => {
      if (high - low <= 1) {
        if (workingArr[high] < workingArr[low]) {
          [workingArr[low], workingArr[high]] = [workingArr[high], workingArr[low]];
        }
        return [low, high];
      }

      let mid = low;
      const pivot = workingArr[high];

      while (mid <= high) {
        steps.push({ arr: [...workingArr], comparing: [mid, high] });

        if (workingArr[mid] < pivot) {
          [workingArr[low], workingArr[mid]] = [workingArr[mid], workingArr[low]];
          low++;
          mid++;
        } else if (workingArr[mid] === pivot) {
          mid++;
        } else {
          [workingArr[mid], workingArr[high]] = [workingArr[high], workingArr[mid]];
          high--;
        }
      }

      return [low - 1, mid];
    };

    const quickSort3 = (low, high) => {
      if (low < high) {
        const [p, q] = partition3(low, high);
        quickSort3(low, p);
        quickSort3(q, high);
      }
    };

    quickSort3(0, workingArr.length - 1);
    return steps;
  },
};

// Data generators (same as before)
const generateData = (type, size = 30) => {
  switch (type) {
    case "random":
      return Array.from({ length: size }, () => Math.floor(Math.random() * 100) + 1);
    case "nearly-sorted":
      const nearlySorted = Array.from({ length: size }, (_, i) => i + 1);
      for (let i = 0; i < 3; i++) {
        const idx1 = Math.floor(Math.random() * size);
        const idx2 = Math.floor(Math.random() * size);
        [nearlySorted[idx1], nearlySorted[idx2]] = [nearlySorted[idx2], nearlySorted[idx1]];
      }
      return nearlySorted;
    case "reversed":
      return Array.from({ length: size }, (_, i) => size - i);
    case "few-unique":
      const values = [10, 30, 50, 70, 90];
      return Array.from({ length: size }, () => values[Math.floor(Math.random() * values.length)]);
    default:
      return Array.from({ length: size }, (_, i) => i + 1);
  }
};

// Icons as components (same as before)
const PlayIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const PauseIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);

const RotateCcwIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

// Button component (same as before)
const Button = ({ children, onClick, className = "", variant = "default", size = "default", ...props }) => {
  const baseClasses = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
  
  const variantClasses = {
    default: "bg-gray-900 text-white hover:bg-gray-700",
    outline: "border border-gray-200 bg-white hover:bg-gray-100 hover:text-gray-900",
    ghost: "hover:bg-gray-100 hover:text-gray-900",
  };
  
  const sizeClasses = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3",
    lg: "h-11 px-8",
  };
  
  return (
    <button
      className={`${baseClasses} ${variantClasses[variant] || variantClasses.default} ${sizeClasses[size] || sizeClasses.default} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Visualization component (same as before)
const SortingVisualization = ({
  algorithm,
  dataType,
  isPlaying,
  onPlay,
  onPause,
  onReset,
}) => {
  const canvasRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [data, setData] = useState([]);
  const animationRef = useRef();

  const initializeData = useCallback(() => {
    const newData = generateData(dataType, 30);
    setData(newData);
    const algorithmSteps = sortingAlgorithms[algorithm](newData, () => {});
    setSteps([{ arr: newData }, ...algorithmSteps]);
    setCurrentStep(0);
  }, [algorithm, dataType]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  useEffect(() => {
    if (isPlaying && currentStep < steps.length - 1) {
      animationRef.current = setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
      }, 100);
    } else if (currentStep >= steps.length - 1) {
      onPause();
    }

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, [isPlaying, currentStep, steps.length, onPause]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || steps.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const step = steps[currentStep];
    if (!step) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const barWidth = canvas.width / step.arr.length;
    const maxValue = Math.max(...step.arr);

    step.arr.forEach((value, index) => {
      const barHeight = (value / maxValue) * (canvas.height - 10);
      const x = index * barWidth;
      const y = canvas.height - barHeight;

      let color = "#e5e7eb";
      if (step.comparing && step.comparing.includes(index)) {
        color = "#ef4444";
      } else if (step.sorted && step.sorted.includes(index)) {
        color = "#22c55e";
      }

      ctx.fillStyle = color;
      ctx.fillRect(x + 1, y, barWidth - 2, barHeight);

      ctx.strokeStyle = "#374151";
      ctx.lineWidth = 0.5;
      ctx.strokeRect(x + 1, y, barWidth - 2, barHeight);
    });
  }, [currentStep, steps]);

  const handleReset = () => {
    onReset();
    initializeData();
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 dark:bg-gray-800">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200 capitalize">
          {algorithm.replace(/([A-Z])/g, " $1").trim()}
        </span>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={isPlaying ? onPause : onPlay}>
            {isPlaying ? <PauseIcon className="h-3 w-3" /> : <PlayIcon className="h-3 w-3" />}
          </Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={handleReset}>
            <RotateCcwIcon className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <canvas ref={canvasRef} width={200} height={120} className="w-full border border-gray-100 dark:border-gray-700 rounded" />
      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">
        Step {currentStep + 1} of {steps.length}
      </div>
    </div>
  );
};

export default function SortingVisualizer() {
  const [playingStates, setPlayingStates] = useState({});
  const [playAll, setPlayAll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const algorithms = ["insertion", "selection", "bubble", "shell", "merge", "heap", "quick", "quick3"];
  const dataTypes = ["random", "nearly-sorted", "reversed", "few-unique"];

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  const handlePlay = (algorithm, dataType) => {
    const key = `${algorithm}-${dataType}`;
    setPlayingStates((prev) => ({ ...prev, [key]: true }));
  };

  const handlePause = (algorithm, dataType) => {
    const key = `${algorithm}-${dataType}`;
    setPlayingStates((prev) => ({ ...prev, [key]: false }));
  };

  const handleReset = (algorithm, dataType) => {
    const key = `${algorithm}-${dataType}`;
    setPlayingStates((prev) => ({ ...prev, [key]: false }));
  };

  const handlePlayAll = () => {
    setPlayAll(!playAll);
    const newStates = {};

    if (!playAll) {
      algorithms.forEach((algorithm) => {
        dataTypes.forEach((dataType) => {
          const key = `${algorithm}-${dataType}`;
          newStates[key] = true;
        });
      });
    }

    setPlayingStates(newStates);
  };

  // Mobile view - vertical layout
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-4">
              <h1 className="mb-2 text-xl font-bold text-gray-900">Sorting Algorithms</h1>
              <div className="mb-3 text-sm text-gray-600">
                <span className="font-medium">How to use:</span> Tap "Play all" or individual play buttons to animate.
              </div>
              
              <Button
                onClick={handlePlayAll}
                className="flex items-center gap-2 text-sm w-full justify-center mb-4"
                variant={playAll ? "default" : "outline"}
              >
                <PlayIcon className="h-4 w-4" />
                {playAll ? "Pause All" : "Play All"}
              </Button>
            </div>

            {dataTypes.map((dataType) => (
              <div key={dataType} className="mb-6">
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-sm font-medium capitalize text-gray-700">
                    {dataType.replace("-", " ")}
                  </h2>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1"
                    onClick={() => {
                      const anyPlaying = algorithms.some(alg => playingStates[`${alg}-${dataType}`]);
                      algorithms.forEach((algorithm) => {
                        if (anyPlaying) {
                          handlePause(algorithm, dataType);
                        } else {
                          handlePlay(algorithm, dataType);
                        }
                      });
                    }}
                  >
                    <PlayIcon className="h-3 w-3" />
                    <span>{algorithms.some(alg => playingStates[`${alg}-${dataType}`]) ? "Pause" : "Play"}</span>
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {algorithms.map((algorithm) => {
                    const key = `${algorithm}-${dataType}`;
                    return (
                      <SortingVisualization
                        key={key}
                        algorithm={algorithm}
                        dataType={dataType}
                        isPlaying={playingStates[key] || false}
                        onPlay={() => handlePlay(algorithm, dataType)}
                        onPause={() => handlePause(algorithm, dataType)}
                        onReset={() => handleReset(algorithm, dataType)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="mt-4 text-xs text-gray-500">
              <p>Red bars = comparing, Green bars = sorted</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop view - horizontal layout
  return (
    <>
      <HeaderSection toolId="sortingVisualizer" />
      <div className="max-w-4xl mx-auto p-4  lg:max-w-full lg:px-4">
        <div className="bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 overflow-hidden">
         <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                                <h1 className="text-2xl font-bold mb-2 flex items-center">
                                    <FontAwesomeIcon
                                        icon={faSort}
                                        className="mr-3"
                                    />
                                    Sorting Visualizer
                                </h1>
                                <p className="opacity-90">
                                    Visualize sorting algorithms in real-time with
                                    comprehensive validation checks
                                </p>
                            </div>
          <div className="dark:bg-gray-800 dark:border-gray-700 border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6">
            <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">Sorting Algorithms Visualization</h1>
            <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              <span className="font-medium">How to use:</span> Press "Play all", or choose the{" "}
              <PlayIcon className="inline h-4 w-4 text-blue-600 dark:text-blue-400" /> button for the individual row/column to animate.
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              {/* Header row */}
              <div className="grid grid-cols-9 gap-2">
                <div className="flex items-center justify-center p-2">
                  <Button
                    onClick={handlePlayAll}
                    className="flex items-center gap-2 text-sm"
                    variant={playAll ? "default" : "outline"}
                  >
                    <PlayIcon className="h-4 w-4" />
                    Play All
                  </Button>
                </div>

                {algorithms.map((algorithm) => (
                  <div key={algorithm} className="border-b p-2 text-center text-sm font-medium capitalize text-gray-700">
                    {algorithm === "quick3" ? "Quick3" : algorithm}
                  </div>
                ))}
              </div>

              {/* Data type rows */}
              {dataTypes.map((dataType) => (
                <div key={dataType} className="grid grid-cols-9 gap-2 border-t py-2">
                  <div className="flex items-center">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-1 text-sm"
                      onClick={() => {
                        const anyPlaying = algorithms.some(alg => playingStates[`${alg}-${dataType}`]);
                        algorithms.forEach((algorithm) => {
                          if (anyPlaying) {
                            handlePause(algorithm, dataType);
                          } else {
                            handlePlay(algorithm, dataType);
                          }
                        });
                      }}
                    >
                      <PlayIcon className="h-3 w-3" />
                      <span className="capitalize">{dataType.replace("-", " ")}</span>
                    </Button>
                  </div>

                  {algorithms.map((algorithm) => {
                    const key = `${algorithm}-${dataType}`;
                    return (
                      <SortingVisualization
                        key={key}
                        algorithm={algorithm}
                        dataType={dataType}
                        isPlaying={playingStates[key] || false}
                        onPlay={() => handlePlay(algorithm, dataType)}
                        onPause={() => handlePause(algorithm, dataType)}
                        onReset={() => handleReset(algorithm, dataType)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 text-xs text-gray-500">
            <p>This visualization shows how different sorting algorithms work with various types of data.</p>
            <p>Red bars indicate elements being compared, green bars indicate sorted elements.</p>
          </div>
          </div>
        </div>
      </div>
      <BottomSection toolId="sortingVisualizer" />
    </>
  );
}